import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Container, Row, Stack } from 'react-bootstrap'
import { PencilSquare } from 'react-bootstrap-icons'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'
import Select from 'react-select'
import { ToastContainer } from 'react-toastify'
import { DayPicker } from 'react-day-picker'
import { format } from 'date-fns/format'
import { ru } from 'date-fns/locale'
import Modal from 'react-bootstrap/Modal'

import Form from 'react-bootstrap/esm/Form'

import CustomSelect from '../../components/_atoms/CustomSelect/CustomSelect'
import Loader from '../../components/_atoms/Loader/Loader'
import OverlayWithText from '../../components/_atoms/OverlayWithText/OverlayWithText'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import { ModalChoseParts } from '../../components/_molecules/ModalChoseParts/ModalChoseParts'
import { ModalSaveClient } from '../../components/_molecules/ModalSaveClient/ModalSaveClient'
import ModalViewPart from '../../components/_molecules/ModalViewPart/ModalViewPart'
import { fontSizes, fontWeights } from '../../constants/salesStyles'
import { ToastType, useToast } from '../../hooks/useToast'
import { TAX_RATES } from '../../modules/redux/documentList/PrintFiscalReceipt'
import { useLazyStatusQuery, usePrintFiscalReceiptMutation } from '../../modules/store/atolApi/atolApi'
import { useSelector } from '../../modules/store/customSelector'
import { useGetPartsAdminListMutation } from '../../modules/store/datahubApi/datahubApi'
import { useUpdateSoldPartsFromDealMutation } from '../../modules/store/datahubApi/datahubApi'
import {
    useLazyGetClientsListQuery,
    useGetDealsListMutation,
    useGetDealStatusDictQuery,
    useSaveDealMutation,
    useDownloadDealDocumentMutation,
} from '../../modules/store/dochubApi/dochubApi'
import {
    Client,
    Deal,
    DealClient,
    DealItem,
    DealStatus,
    DealStatusInfo,
    Payment,
    SaveDealApiArg,
} from '../../modules/store/dochubApi/dochubTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { IOption } from '../../types/common/commonTypes'
import { Part, paySources, sources } from '../../types/part/partTypes'
import { formatPrice } from '../../utils/formatAndUnformatPrice'
import { getCurrencySymbol } from '../../utils/getCurrencySymbol'

import { FormRow } from './FormRow/FormRow'
import { ModalPayment } from './ModalPayment/ModalPayment'
import { PartsTable } from './PartsTable/PartsTable'
import styles from './styles.module.scss'

interface FormData {
    price: number
    client: Client | DealClient | null
    seller: string
    selectedSource: IOption | null
    selectedPaySource: IOption | null
    comment: string
    selectedStatus: DealStatusInfo | null
    soldAt: Date | null
}

const DealPage = () => {
    const navigate = useNavigate()
    const { dealId } = useParams<{ dealId?: string }>()
    const userPhoneNumber = useSelector(state => state.userData.smsAuth.phoneNumber)
    const { register, handleSubmit, control, watch, reset, setValue } = useForm<FormData>({
        defaultValues: {
            price: 0,
            client: null,
            seller: userPhoneNumber,
            selectedSource: null,
            selectedPaySource: null,
            comment: '',
            selectedStatus: null,
            soldAt: null,
        },
    })
    const clientWatch = watch('client')
    const selectedPaySource = watch('selectedPaySource')

    const userId = useMemo(() => localStorage.getItem('userId')!, [])

    const [currentDeal, setCurrentDeal] = useState<Deal | null>(null)
    const [totalPrice, setTotalPrice] = useState<number>(0)
    const [selectedParts, setSelectedParts] = useState<Part[]>([])
    const [partsData, setPartsData] = useState<Record<string, { price: number; qty: number }>>({})
    const [currentPart, setCurrentPart] = useState<Part | null>(null)
    const [currentPayments, setCurrentPayments] = useState<Payment[] | null>(null)

    // Модалки
    const [isModalChosePartsOpen, setModalChosePartsOpen] = useState(false)
    const [isModalSaveClientOpen, setModalSaveClientOpen] = useState(false)
    const [isModalViewPartOpen, setModalViewPartOpen] = useState(false)
    const [isModalPaymentOpen, setModalPaymentOpen] = useState(false)
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

    const [saveDeal, { data: savedDealData, isLoading: isLoadingSaveDeal, isError: isErrorSaveDeal }] =
        useSaveDealMutation()
    const [getDealsList, { data: dealListData, isLoading: isLoadingGetDealsList, isError: isErrorGetDealsList }] =
        useGetDealsListMutation()
    const [getPartsAdminList, { data: partsListData, isLoading: isLoadingGetPartsList, isError: isErrorGetPartsList }] =
        useGetPartsAdminListMutation()

    const [updateSoldPartsFromDeal] = useUpdateSoldPartsFromDealMutation()

    const {
        data: dealStatusDictData,
        isLoading: isLoadingGetDealStatusDict,
        isError: isErrorGetDealStatusDict,
    } = useGetDealStatusDictQuery()
    const [
        printFiscalReceipt,
        { data: printFiscalReceiptData, isLoading: isLoadingPrintFiscalReceipt, isError: isErrorPrintFiscalReceipt },
    ] = usePrintFiscalReceiptMutation()
    const [getStatus, { data: statusData }] = useLazyStatusQuery()
    const [getClientsList, { data: clientsList, isFetching: isLoadingGetClientsList, isError: isErrorGetClientsList }] =
        useLazyGetClientsListQuery()
    const [downloadDealDocument, { isLoading: isLoadingDownloadDealDocument, isError: isErrorDownloadDealDocument }] =
        useDownloadDealDocumentMutation()

    const handleSearchClients = useCallback(
        (search: string) => {
            getClientsList({ params: { pagination: { page: 1, perPage: 10 }, search } })
        },
        [getClientsList],
    )

    const cashier = useSelector(state => state.userData.cashierInfo)?.cashier
    const currentCurrency = useSelector(state => state.partList.currentCurrency)

    const isPrintFiscalReceiptButtonDisabled = useMemo(
        () => !cashier || !selectedParts.length || isLoadingPrintFiscalReceipt || !statusData || !clientWatch,
        [cashier, clientWatch, isLoadingPrintFiscalReceipt, selectedParts.length, statusData],
    )

    useEffect(() => {
        cashier && getStatus()
    }, [cashier, getStatus])

    const removePart = useCallback((removedPart: Part) => {
        setSelectedParts(prev => prev.filter(part => part.id !== removedPart.id))
    }, [])

    const updatePartData = (id: string, field: 'price' | 'qty', value: number) => {
        setPartsData(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value,
            },
        }))
    }

    const resetState = useCallback(() => {
        reset()
        setSelectedParts([])
        setTotalPrice(0)
        setPartsData({})
        setCurrentDeal(null)
    }, [reset])

    const onSubmit = useCallback(
        (data: FormData) => {
            const dealItems: DealItem[] = selectedParts.map(part => ({
                title: part.customTitle || part.title,
                price: partsData[part.id]?.price ?? part.price,
                qty: partsData[part.id]?.qty ?? 1,
                part: {
                    id: part.id,
                    vendorCode: part.vendorCode,
                    customTitle: part.customTitle,
                    title: part.title,
                    markTitle: part.markTitle,
                    modelTitle: part.modelTitle,
                    generationTitle: part.generationTitle,
                    warehouse: {
                        title: part.warehouseData?.title,
                        place: part.extraData?.warehouse?.place,
                        row: part.extraData?.warehouse?.row,
                        shelf: part.extraData?.warehouse?.shelf,
                    },
                },
            }))

            const dealData: Omit<SaveDealApiArg, 'userId'> = {
                deal: {
                    userId,
                    code: currentDeal ? currentDeal.code : 'code',
                    status: data.selectedStatus?.code || DealStatus.reserved,
                    items: dealItems,
                    ...(data.client && {
                        client: {
                            id: data.client.id,
                            name: data.client.name,
                            phone: data.client.phone || '',
                        },
                    }),
                    seller: {
                        name: data.seller,
                    },
                    meta: {
                        comment: data.comment,
                        source: data.selectedSource!.value,
                        ...(currentPayments
                            ? { payments: currentPayments }
                            : { paySource: data.selectedPaySource?.value || null }),
                    },
                    ...(data.soldAt && !isNaN(data.soldAt.getTime()) && { soldAt: data.soldAt.getTime() }),
                },
            }

            if (currentDeal) {
                dealData.deal.id = currentDeal.id
            }

            saveDeal(dealData)
                .unwrap()
                .then(response => {
                    // Если сделка успешно сохранена и получен dealId
                    const dealId = response.data.id
                    // Формируем payload для updateSoldPartsFromDeal
                    const updatePayload = {
                        userId,
                        dealId,
                        dealStatus: response.data.status,
                        items: selectedParts.map(part => ({
                            partId: part.id,
                            marketplace: data.selectedSource ? data.selectedSource.value : 'avito', // можно заменить на константу, если требуется
                            price: partsData[part.id]?.price ?? part.price,
                            qty: partsData[part.id]?.qty ?? 1,
                        })),
                    }
                    updateSoldPartsFromDeal(updatePayload)
                        .unwrap()
                        .then(() => {
                            // При необходимости можно добавить уведомление об успехе
                        })
                        .catch(err => {
                            console.error('Ошибка updateSoldPartsFromDeal: ', err)
                        })
                })
                .catch(err => {
                    console.error('Ошибка сохранения сделки: ', err)
                })
        },
        [currentDeal, currentPayments, partsData, saveDeal, selectedParts],
    )

    const handlePrintFiscalReceipt = useCallback(
        (data: FormData) => {
            if (!cashier) return
            if (!data.client) return

            const totalTax = totalPrice * (TAX_RATES[cashier.tax] ?? 0)
            const totalSubtotal = totalPrice - totalTax
            const soldParts = selectedParts.map(part => ({
                name: part.customTitle ?? part.title,
                quantity: partsData[part.id]?.qty ?? 1,
                price: partsData[part.id]?.price ?? part.price,
                tax: cashier.tax,
            }))

            printFiscalReceipt({
                cash_register_id: '',
                transaction_id: currentDeal?.code ?? null,
                operator: `${cashier.familyName} ${cashier.name} ${cashier.surname}`,
                items: soldParts,
                total: {
                    subtotal: Number(totalSubtotal.toFixed(2)),
                    tax: Number(totalTax.toFixed(2)),
                    total_amount: totalPrice,
                },
                payment: {
                    method: 'cash',
                    amount: totalPrice,
                },
                customer: {
                    name: data.client.name,
                },
                receipt_options: {
                    print_copies: 1,
                },
            })
        },
        [cashier, currentDeal?.code, partsData, printFiscalReceipt, selectedParts, totalPrice],
    )

    const handleDocumentDownload = useCallback(
        (currentDeal: Deal) => {
            downloadDealDocument({ dealId: currentDeal.id })
        },
        [downloadDealDocument],
    )

    useEffect(() => {
        setTotalPrice(
            selectedParts.reduce(
                (total, part) => total + (partsData[part.id]?.price ?? part.price) * (partsData[part.id]?.qty ?? 1),
                0,
            ),
        )
    }, [partsData, selectedParts])

    useEffect(() => {
        if (dealId) {
            getDealsList({ params: { id: dealId, pagination: { page: 1, perPage: 1 } } })
        } else {
            resetState()
        }
    }, [dealId, getDealsList, resetState])

    useEffect(() => {
        dealListData && setCurrentDeal(dealListData.data[0])
    }, [dealListData])

    useEffect(() => {
        if (currentDeal) {
            setValue('client', currentDeal.client || null)
            setValue('seller', currentDeal.seller?.name || '')
            setValue('selectedSource', sources.find(source => source.value === currentDeal.meta?.source) || null)
            setValue('selectedPaySource', paySources.find(ps => ps.value === currentDeal.meta?.paySource) || null)
            setValue('comment', currentDeal.meta?.comment || '')
            setValue('selectedStatus', dealStatusDictData ? dealStatusDictData.data[currentDeal.status] : null)
            if (currentDeal.soldAt) {
                const soldAtTimestamp = typeof currentDeal.soldAt === 'string' ? parseInt(currentDeal.soldAt, 10) : currentDeal.soldAt
                if (soldAtTimestamp && !isNaN(soldAtTimestamp)) {
                    const soldAtDate = new Date(soldAtTimestamp)
                    setValue('soldAt', !isNaN(soldAtDate.getTime()) ? soldAtDate : null)
                } else {
                    setValue('soldAt', null)
                }
            } else {
                setValue('soldAt', null)
            }
            currentDeal.meta?.payments && setCurrentPayments(currentDeal.meta.payments)

            const { items } = currentDeal
            if (items) {
                const partsId: string[] = []
                items.forEach(item => {
                    partsId.push(item.part.id)
                    updatePartData(item.part.id, 'price', item.price)
                    updatePartData(item.part.id, 'qty', item.qty)
                })
                getPartsAdminList({ params: { partId: partsId, allStatuses: true } })
            }
        }
    }, [currentDeal, getPartsAdminList, resetState, setValue])

    useEffect(() => {
        partsListData && setSelectedParts(partsListData.data)
    }, [partsListData])

    useEffect(() => {
        if (savedDealData && !currentDeal) {
            navigate(getAdminNavigationPath(adminRouteAlias.deals.edit) + `/${savedDealData.data.id}`)
        }
    }, [savedDealData])

    useToast({ isTriggered: !!savedDealData, type: ToastType.SUCCESS, message: `Сделка успешно сохранена` })
    useToast({ isTriggered: isErrorSaveDeal, type: ToastType.ERROR, message: `Ошибка сохранения сделки` })
    useToast({ isTriggered: isErrorGetDealsList, type: ToastType.ERROR, message: 'Ошибка загрузки сделок' })
    useToast({ isTriggered: isErrorGetPartsList, type: ToastType.ERROR, message: 'Ошибка загрузки запчастей' })
    useToast({
        isTriggered: isErrorGetDealStatusDict,
        type: ToastType.ERROR,
        message: 'Ошибка загрузки словаря статусов',
    })
    // TODO: Надо учесть ответ при успешном создании чека
    // useToast({
    //     isTriggered: !!printFiscalReceiptData,
    //     type: ToastType.INFO,
    //     message: printFiscalReceiptData.data.error_description,
    // })
    useToast({
        isTriggered: isErrorPrintFiscalReceipt,
        type: ToastType.ERROR,
        message: 'Ошибка печатания чека',
    })
    useToast({ isTriggered: isErrorDownloadDealDocument, type: ToastType.ERROR, message: 'Ошибка загрузки документа' })

    return (
        <div className={styles.wrap}>
            <ToastContainer position="bottom-center" autoClose={5000} />
            {(isLoadingSaveDeal || isLoadingGetDealsList) && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={100} thicknessInPx={10} />
                </OverlayWithText>
            )}
            {isModalChosePartsOpen && (
                <ModalChoseParts
                    isOpen={isModalChosePartsOpen}
                    closeModal={() => setModalChosePartsOpen(false)}
                    setSelectedParts={newSelectedParts => setSelectedParts(prev => [...prev, ...newSelectedParts])}
                    disabledParts={selectedParts}
                />
            )}
            {isModalSaveClientOpen && (
                <ModalSaveClient
                    isOpen={isModalSaveClientOpen}
                    closeModal={() => setModalSaveClientOpen(false)}
                    onSuccess={() => handleSearchClients('')}
                />
            )}
            {currentPart && isModalViewPartOpen && (
                <ModalViewPart
                    part={currentPart}
                    isOpen={isModalViewPartOpen}
                    closeModal={() => setModalViewPartOpen(false)}
                />
            )}
            {isModalPaymentOpen && (
                <ModalPayment
                    isOpen={isModalPaymentOpen}
                    closeModal={() => setModalPaymentOpen(false)}
                    paySources={paySources}
                    totalPrice={totalPrice}
                    initialPayments={
                        currentPayments ?? [
                            {
                                date: new Date().toLocaleDateString(),
                                paySource: selectedPaySource ? selectedPaySource.value : null,
                                amount: totalPrice,
                            },
                        ]
                    }
                    onSave={setCurrentPayments}
                />
            )}

            <Stack direction="horizontal">
                <SalesText
                    text={currentDeal ? `Сделка № ${currentDeal.code}` : 'Новая сделка'}
                    fontSize={fontSizes.xxl}
                    fontWeight={fontWeights.bold}
                />
            </Stack>
            <Container fluid className={styles.topContainer}>
                <Row>
                    <Col xs={7}>
                        <FormRow
                            label="Покупатель"
                            button={
                                <Button variant="primary" onClick={() => setModalSaveClientOpen(true)}>
                                    +
                                </Button>
                            }
                            className={'mb-4'}
                        >
                            <Controller
                                name="client"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <CustomSelect
                                        {...field}
                                        onSearch={handleSearchClients}
                                        options={clientsList?.data}
                                        isLoading={isLoadingGetClientsList}
                                        getOptionLabel={option => option.name}
                                        getOptionValue={option => option.id}
                                        placeholder="Выберите покупателя"
                                    />
                                )}
                            />
                        </FormRow>
                        <FormRow label="Ответственный" className={'mb-4'}>
                            <Form.Control
                                type="text"
                                {...register('seller', { required: true })}
                                placeholder="Введите ответственного"
                            />
                        </FormRow>
                        <FormRow label="Источник сделки" className={'mb-4'}>
                            <Controller
                                name="selectedSource"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select {...field} options={sources} placeholder="Выберите источник" isClearable />
                                )}
                            />
                        </FormRow>
                        <FormRow
                            label="Источник оплаты"
                            button={
                                <Button variant="primary" onClick={() => setModalPaymentOpen(true)}>
                                    <PencilSquare size={20} />
                                </Button>
                            }
                            className={'mb-4'}
                        >
                            {currentPayments ? (
                                <div className="d-flex flex-column gap-4">
                                    {currentPayments.map((payment, index) => (
                                        <Select
                                            key={index}
                                            value={paySources.find(ps => ps.value === payment.paySource) || null}
                                            options={paySources}
                                            onChange={option => {
                                                setCurrentPayments(prev => {
                                                    if (!prev) return prev

                                                    const updated = [...prev]
                                                    updated[index] = {
                                                        ...updated[index],
                                                        paySource: option?.value || null,
                                                    }
                                                    return updated
                                                })
                                            }}
                                            placeholder="Выберите источник оплаты"
                                            isClearable
                                            formatOptionLabel={(option, { context }) => (
                                                <div>
                                                    {option.label}
                                                    {context === 'value' && !isNaN(payment.amount) && (
                                                        <span className="text-muted ms-1">- {payment.amount}</span>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <Controller
                                    name="selectedPaySource"
                                    control={control}
                                    rules={{ required: false }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            options={paySources}
                                            placeholder="Выберите оплату"
                                            isClearable
                                        />
                                    )}
                                />
                            )}
                        </FormRow>

                        <FormRow label="Дата продажи" className={'mb-4'}>
                            <Controller
                                name="soldAt"
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <Form.Control
                                            type="text"
                                            readOnly
                                            value={
                                                field.value && field.value instanceof Date && !isNaN(field.value.getTime())
                                                    ? format(field.value, 'dd.MM.yyyy', { locale: ru })
                                                    : ''
                                            }
                                            placeholder="dd.mm.yyyy"
                                            onClick={() => setIsDatePickerOpen(true)}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        {isDatePickerOpen && (
                                            <Modal
                                                show={isDatePickerOpen}
                                                onHide={() => setIsDatePickerOpen(false)}
                                                centered
                                            >
                                                <Modal.Header closeButton>
                                                    <Modal.Title>Выберите дату продажи</Modal.Title>
                                                </Modal.Header>
                                                <Modal.Body>
                                                    <DayPicker
                                                        animate
                                                        captionLayout="dropdown"
                                                        mode="single"
                                                        selected={field.value || undefined}
                                                        onSelect={date => {
                                                            field.onChange(date || null)
                                                        }}
                                                        navLayout="around"
                                                        locale={ru}
                                                        defaultMonth={field.value || new Date()}
                                                    />
                                                </Modal.Body>
                                                <Modal.Footer>
                                                    <Button variant="outline-secondary" onClick={() => {
                                                        field.onChange(null)
                                                    }}>
                                                        Сбросить
                                                    </Button>
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => {
                                                            setIsDatePickerOpen(false)
                                                        }}
                                                    >
                                                        Применить
                                                    </Button>
                                                </Modal.Footer>
                                            </Modal>
                                        )}
                                    </>
                                )}
                            />
                        </FormRow>
                        <FormRow
                            label="Статус сделки"
                            button={
                                <Button
                                    variant="primary"
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={!selectedParts.length || isLoadingGetPartsList}
                                >
                                    {currentDeal ? 'Сохранить' : 'Создать сделку'}
                                </Button>
                            }
                        >
                            <Controller
                                name="selectedStatus"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={dealStatusDictData ? Object.values(dealStatusDictData.data) : []}
                                        isLoading={isLoadingGetDealStatusDict}
                                        isDisabled={isErrorGetDealStatusDict}
                                        getOptionLabel={option => option.name}
                                        getOptionValue={option => option.code}
                                        placeholder="Выберите статус сделки"
                                        isClearable
                                    />
                                )}
                            />
                        </FormRow>
                    </Col>
                    <Col>
                        <Form.Group>
                            <Form.Label>Комментарии к сделке</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={7}
                                placeholder="Введите комментарий"
                                {...register('comment')}
                                style={{ resize: 'none' }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Container>
            <Container fluid style={{ padding: 0 }}>
                <Row>
                    <Col>
                        <Stack direction="horizontal" gap={5}>
                            <SalesText
                                text={'Запчасти в сделке'}
                                fontSize={fontSizes.l}
                                fontWeight={fontWeights.bold}
                            />
                            <Button
                                variant="primary"
                                style={{ width: '130px' }}
                                onClick={() => setModalChosePartsOpen(true)}
                            >
                                + Добавить
                            </Button>
                        </Stack>
                    </Col>
                    <Col>
                        <Stack direction="horizontal" gap={5}>
                            <SalesText
                                text={`${formatPrice(totalPrice)} ${getCurrencySymbol(currentCurrency)}`}
                                fontSize={fontSizes.xl}
                                fontWeight={fontWeights.bold}
                            />
                            <SalesText text={`Сумма сделки`} fontSize={fontSizes.l} fontWeight={fontWeights.bold} />
                        </Stack>
                    </Col>
                    {currentDeal && (
                        <>
                            <Col xs="auto">
                                <Button
                                    variant="outline-primary"
                                    onClick={() => handleDocumentDownload(currentDeal)}
                                    disabled={isLoadingDownloadDealDocument}
                                >
                                    🖨 чек/наклад
                                </Button>
                            </Col>
                            <Col xs="auto">
                                <Button
                                    variant="outline-primary"
                                    disabled={isPrintFiscalReceiptButtonDisabled}
                                    onClick={handleSubmit(handlePrintFiscalReceipt)}
                                >
                                    Пробить чек
                                </Button>
                            </Col>
                        </>
                    )}
                </Row>
            </Container>

            <PartsTable
                parts={selectedParts}
                removePart={removePart}
                updatePartData={updatePartData}
                partsData={partsData}
                isLoading={isLoadingGetPartsList}
                onPartClick={part => {
                    setCurrentPart(part)
                    setModalViewPartOpen(true)
                }}
            />
        </div>
    )
}

export default DealPage
